import React from 'react';

import { UserPreferences } from '@/DTOs/userPreferences';
import logRocketPromise, { LogRocketInstance } from '@/services/logRocket';

export interface IUserTraits {
  readonly name?: string;
  readonly businessName?: string;
  readonly email?: string;
  readonly country?: string;
  readonly integratorId?: string | null;
  readonly group?: string;
  readonly language?: UserPreferences['language'];
  readonly timezone?: UserPreferences['timezone'];
  readonly theme?: NonNullable<UserPreferences['appearance']>['theme'];
}

interface LogRocketHook {
  /**
   * Identifies the user in LogRocket sessions. Associates a unique user ID with the session
   * and optionally includes user traits for filtering and analysis.
   *
   * Best practices:
   * - Use a stable, immutable user ID (e.g., database ID, Keycloak ID)
   * - Only call when the user is authenticated (not for anonymous sessions)
   * - Include searchable traits like name, email, and business context
   *
   * @param userId - The unique, immutable identifier for the user (e.g., Keycloak ID)
   * @param userTraits - Optional object containing user information for filtering and search
   */
  identify: (userId: string, userTraits?: IUserTraits) => void;
}

export const useLogRocket = (): LogRocketHook =>
  React.useMemo(
    (): LogRocketHook => ({
      identify: (userId: string, userTraits?: IUserTraits): void => {
        const cleanTraits = userTraits
          ? Object.fromEntries(
              Object.entries(userTraits).filter(
                ([_key, value]: [string, unknown]): boolean =>
                  value !== undefined && value !== null,
              ),
            )
          : undefined;

        void logRocketPromise.then((logRocket: LogRocketInstance): void =>
          logRocket.identify(userId, cleanTraits),
        );
      },
    }),
    [],
  );
